import { AxiosInstance, AxiosResponse } from 'axios';

export const getCompany = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<Company>> => {
  const response: AxiosResponse<ApiResponse<Company>> = await axiosInstance.get(
    '/company'
  );
  return response.data;
};

export const getCompanyName = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<string>> => {
  const response: AxiosResponse<ApiResponse<string>> = await axiosInstance.get(
    '/company/name'
  );
  return response.data;
};

export const getCodes = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CompanyCode>> => {
  const response: AxiosResponse<ApiResponse<CompanyCode>> =
    await axiosInstance.get('/company/codes');
  return response.data;
};

export const getEmployees = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<PublicEmployeeProfile[]>> => {
  const response: AxiosResponse<ApiResponse<PublicEmployeeProfile[]>> =
    await axiosInstance.get('/company/employees');
  return response.data;
};

export const getBirthdays = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<BirthdayPermission[]>> => {
  const response: AxiosResponse<ApiResponse<BirthdayPermission[]>> =
    await axiosInstance.get('/company/birthdays');
  return response.data;
};

export const getConfig = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CompanyConfig[]>> => {
  const response: AxiosResponse<ApiResponse<CompanyConfig[]>> =
    await axiosInstance.get('/company/config');
  return response.data;
};

export const getPtoRequests = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<PtoRequest[]>> => {
  const response: AxiosResponse<ApiResponse<PtoRequest[]>> =
    await axiosInstance.get('/company/pto_requests');
  return response.data;
};

export const updateConfig = async (
  axiosInstance: AxiosInstance,
  config: CompanyConfig
): Promise<ApiResponse<CompanyConfig>> => {
  const response: AxiosResponse<ApiResponse<CompanyConfig>> =
    await axiosInstance.post('/company/config', config);
  return response.data;
};

export const uploadLogo = async (
  axiosInstance: AxiosInstance,
  formData: FormData
): Promise<ApiResponse<any>> => {
  const response = await axiosInstance.post(`/company/upload_logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

export const getActivePrismUsers = async (
  axiosInstance: AxiosInstance,
  prismUserType?: readablePrismUserTypes
): Promise<ApiResponse<PrismSecurityUser[]>> => {
  const response: AxiosResponse<ApiResponse<PrismSecurityUser[]>> =
    await axiosInstance.get('/company/active-prism-users', {
      params: { user_type: prismUserType },
    });
  return response.data;
};

export const getLaborNotice = async (
  axiosInstance: AxiosInstance
): Promise<Blob> => {
  const response = await axiosInstance.get('/company/labor_notice', {
    responseType: 'blob',
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Handbook CRUD
// ---------------------------------------------------------------------------

export const getCompanyHandbooks = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<HandbookAsset[]>> => {
  const response: AxiosResponse<ApiResponse<HandbookAsset[]>> =
    await axiosInstance.get('/company/handbooks');
  return response.data;
};

export const uploadHandbook = async (
  axiosInstance: AxiosInstance,
  formData: FormData
): Promise<ApiResponse<any>> => {
  const response = await axiosInstance.post('/company/handbooks', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteHandbook = async (
  axiosInstance: AxiosInstance,
  handbook_id: number
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.delete(`/company/handbooks/${handbook_id}`);
  return response.data;
};

export const archiveEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  handbook_id: number,
  payload: HandbookAsset
): Promise<ApiResponse<HandbookAsset>> => {
  const response: AxiosResponse<ApiResponse<HandbookAsset>> =
    await axiosInstance.put(`/company/handbooks/${handbook_id}/archive`, {
      client_id: payload.client_id,
      asset_type: payload.asset_type,
      archived: payload.archived,
    });
  return response.data;
};

export const assignEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  payload: EmployeeHandbookAssignment[]
): Promise<ApiResponse<EmployeeHandbookAssignment[]>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment[]>> =
    await axiosInstance.post('/company/handbooks/assign', payload);
  return response.data;
};

export const getEmployeeAssignments = async (
  axiosInstance: AxiosInstance,
  handbook_id: number
): Promise<ApiResponse<EmployeeHandbookAssignment[]>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment[]>> =
    await axiosInstance.get(`/company/handbooks/assign/${handbook_id}`);
  return response.data;
};

export const updateAssignmentDeadline = async (
  axiosInstance: AxiosInstance,
  assignmentId: number,
  payload: { due_date: string | null }
): Promise<ApiResponse<EmployeeHandbookAssignment>> => {
  const response: AxiosResponse<ApiResponse<EmployeeHandbookAssignment>> =
    await axiosInstance.patch(
      `/company/handbooks/assign/${assignmentId}`,
      payload
    );
  return response.data;
};

export const unassignEmployeeHandbook = async (
  axiosInstance: AxiosInstance,
  payload: number[]
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.post('/company/handbooks/unassign', payload);
  return response.data;
};

export const companyService = {
  getCodes,
  getCompany,
  getCompanyName,
  getEmployees,
  getBirthdays,
  getConfig,
  getPtoRequests,
  updateConfig,
  uploadLogo,
  getActivePrismUsers,
  getLaborNotice,
  getCompanyHandbooks,
  uploadHandbook,
  deleteHandbook,
  archiveEmployeeHandbook,
  assignEmployeeHandbook,
  getEmployeeAssignments,
  updateAssignmentDeadline,
  unassignEmployeeHandbook,
};
